<template>
	<v-row class="full-height available-inventory-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="available-inventory-toolbar-card" flat>
				<v-card-text class="pa-0">
					<!-- Row 1: Tab Navigation -->
					<div class="inventory-tabs-row">
						<ul class="inventory-tabs">
							<li class="inventory-tabs-tab" :class="{ active: activeTab == 1 }" @click="switchTab(1)">
								Stocked Items
							</li>
							<li class="inventory-tabs-tab" :class="{ active: activeTab == 2 }" @click="switchTab(2)">
								All Items
							</li>
						</ul>
					</div>

					<!-- Row 2: Search and Filters -->
					<v-row align="center" class="px-3 py-2" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.searchTerm"
								:placeholder="activeTab == 1 ? 'Search Stocked Items...' : 'Search All Items...'"
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="available-inventory-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Filter Inactive -->
						<v-col cols="auto">
							<v-checkbox
								class="mt-0 pt-0"
								v-model="searchForm.filterInactive"
								label="Show Inactive Only"
								color="gray"
								hide-details
								@change="onFilterChange"
							></v-checkbox>
						</v-col>

						<!-- Below/near restock -->
						<v-col cols="auto">
							<v-checkbox
								class="mt-0 pt-0"
								v-model="searchForm.belowRestockOnly"
								label="Below/near restock only"
								color="gray"
								hide-details
								@change="onFilterChange"
							></v-checkbox>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 available-inventory-table-container">
			<v-data-table
				:headers="headers"
				:items="inventoryItems"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				:expanded.sync="expandedItems"
				item-key="inventoryItemId"
				class="available-inventory-data-table"
				show-expand
				single-expand
				must-sort
				fixed-header
			>
				<!-- Style Code Column -->
				<template v-slot:item.styleCode="{ item }">
					<div class="d-flex align-center">
						<SystemCategoryIcon :system-category-id="item.systemCategoryId" />
						<span class="ml-2">{{ item.styleCode }}</span>
					</div>
				</template>

				<!-- Available Column - now uses flat column from InventoryListing view -->
				<template v-slot:item.available="{ item }">
					<template v-if="item.infiniteInventory">
						<v-icon>mdi-infinity</v-icon>
					</template>
					<span v-else class="inv-cell" :class="getCellChangeClass(item, 'available')">
						{{ getDisplayAvailable(item.available) }}
					</span>
				</template>

				<!-- On Hand Column - now uses flat column from InventoryListing view -->
				<template v-slot:item.onHand="{ item }">
					<template v-if="item.infiniteInventory">
						<v-icon>mdi-infinity</v-icon>
					</template>
					<span v-else class="inv-cell" :class="getCellChangeClass(item, 'onHand')">
						{{ item.onHand || 0 }}
					</span>
				</template>

				<!-- Committed Column - now uses flat column from InventoryListing view -->
				<template v-slot:item.committed="{ item }">
					<span
						v-if="canEditCommitments && (item.committed || 0) > 0"
						class="committed-link inv-cell"
						:class="getCellChangeClass(item, 'committed')"
						@click.stop="openCommitmentDialog(item, null, '')"
					>
						{{ item.committed || 0 }}
					</span>
					<span v-else class="inv-cell" :class="getCellChangeClass(item, 'committed')">
						{{ item.committed || 0 }}
					</span>
				</template>

				<!-- Need Column - now uses flat column from InventoryListing view -->
				<template v-slot:item.need="{ item }">
					<template v-if="item.infiniteInventory">0</template>
					<span v-else class="inv-cell" :class="getCellChangeClass(item, 'need')">
						{{ item.need || 0 }}
					</span>
				</template>

				<!-- On Order Column - now uses flat column from InventoryListing view -->
				<template v-slot:item.onOrder="{ item }">
					<span class="inv-cell" :class="getCellChangeClass(item, 'onOrder')">
						{{ item.onOrder || 0 }}
					</span>
				</template>

				<!-- Backorder Column - unfulfilled customer demand (ordered - committed) -->
				<template v-slot:item.backorder="{ item }">
					<template v-if="item.infiniteInventory">0</template>
					<span v-else class="inv-cell" :class="getCellChangeClass(item, 'backorder')">
						{{ item.backorder || 0 }}
					</span>
				</template>

				<!-- Future PO Column - placeholder, not currently needed
				<template v-slot:item.futurePo="{ item }">
					{{ item.qtyFuturePo || 0 }}
				</template>
				-->

				<!-- Bin # Column - now uses aggregated bins from InventoryListing view -->
				<template v-slot:item.binNumber="{ item }">
					{{ item.binNumber || '' }}
				</template>

				<template v-slot:item.restockStatus="{ item }">
					<div class="restock-cell">
						<span
							v-if="item.restockStatus === 'Below' || item.restockStatus === 'Near'"
							class="restock-chip"
							:class="item.restockStatus === 'Below' ? 'restock-below' : 'restock-near'"
						>
							● {{ item.restockStatus }}
						</span>
						<!-- Non-sized items have no size sub-table, so surface their item-level
						     restock target directly in this column. -->
						<span
							v-if="!item.hasSizes && !item.infiniteInventory && item.restockQuantity != null"
							class="restock-qty-inline"
						>
							{{ item.restockQuantity }}
						</span>
					</div>
				</template>

				<!-- Expand button - only show if item has sizes -->
				<template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
					<v-btn
						v-if="hasSizes(item)"
						text
						small
						@click="onExpandClick(item, expand, isExpanded)"
						class="text-none"
						:loading="sizeDataLoading[item.inventoryItemId]"
					>
						VIEW SIZES
						<v-icon right>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
					</v-btn>
				</template>

				<!-- Expanded Row Content - Size Chart (loaded on-demand from SizeInventoryInfo view) -->
				<template v-slot:expanded-item="{ headers, item }">
					<td :colspan="headers.length" class="pa-0">
						<div class="inventory-size-chart">
							<!-- Loading state -->
							<div v-if="sizeDataLoading[item.inventoryItemId]" class="text-center pa-4">
								<v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
								<span class="ml-2">Loading size data...</span>
							</div>
							<!-- Size chart table -->
							<v-simple-table v-else dense class="size-chart-table">
								<template v-slot:default>
									<thead>
										<tr class="size-chart-header">
											<th>SIZE</th>
											<th>AVAILABLE</th>
											<th>ON HAND</th>
											<th>COMMITTED</th>
											<th>NEED</th>
											<th>ON ORDER</th>
											<th>BACKORDER</th>
											<th>BIN #</th>
											<th>RESTOCK QTY</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(size, index) in getVisibleSizes(item)"
											:key="size.sizeId || index"
											class="size-chart-row"
											:class="{
												'restock-below-row': size.restockStatus === 'Below',
												'restock-near-row': size.restockStatus === 'Near',
											}"
										>
											<td class="size-name-cell">{{ size.name }}</td>
											<td>
												<template v-if="item.infiniteInventory">
													<v-icon small>mdi-infinity</v-icon>
												</template>
												<template v-else>
													{{ calculateSizeAvailable(size, item) }}
												</template>
											</td>
											<td>
												<template v-if="item.infiniteInventory">
													<v-icon small>mdi-infinity</v-icon>
												</template>
												<template v-else>
													{{ size.onHand || 0 }}
												</template>
											</td>
											<td>{{ size.committed || 0 }}</td>
											<td>
												<template v-if="item.infiniteInventory">0</template>
												<template v-else>{{ size.need || 0 }}</template>
											</td>
											<td>{{ size.onOrder || 0 }}</td>
											<td>
												<template v-if="item.infiniteInventory">0</template>
												<template v-else>{{ size.backorder || 0 }}</template>
											</td>
											<td>{{ size.binNumber || '' }}</td>
											<td>
												<template v-if="item.infiniteInventory">
													<v-icon small>mdi-infinity</v-icon>
												</template>
												<template v-else>
													{{ size.restockQuantity != null ? size.restockQuantity : '—' }}
												</template>
											</td>
										</tr>
										<!-- Total Row -->
										<tr class="size-chart-total-row">
											<td class="size-name-cell">Total</td>
											<td>
												<template v-if="item.infiniteInventory">
													<v-icon small>mdi-infinity</v-icon>
												</template>
												<template v-else>
													{{ getDisplayAvailable(item.available) }}
												</template>
											</td>
											<td>
												<template v-if="item.infiniteInventory">
													<v-icon small>mdi-infinity</v-icon>
												</template>
												<template v-else>
													{{ item.onHand || 0 }}
												</template>
											</td>
											<td>{{ item.committed || 0 }}</td>
											<td>
												<template v-if="item.infiniteInventory">0</template>
												<template v-else>{{ item.need || 0 }}</template>
											</td>
											<td>{{ item.onOrder || 0 }}</td>
											<td>
												<template v-if="item.infiniteInventory">0</template>
												<template v-else>{{ item.backorder || 0 }}</template>
											</td>
											<td></td>
											<td></td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
						</div>
					</td>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
						<p class="mt-2 grey--text">No inventory items found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<commitment-details-dialog
			v-if="commitmentDialogItemId"
			v-model="commitmentDialogOpen"
			:inventory-item-id="commitmentDialogItemId"
			:size-id="commitmentDialogSizeId"
			:size-name="commitmentDialogSizeName"
			:style-code="commitmentDialogStyleCode"
			@removed="onCommitmentsRemoved"
		></commitment-details-dialog>
	</v-row>
</template>

<script>
import { GET_INVENTORY_LISTINGS, GET_SIZE_INVENTORY_INFO } from "@/gql/inventory";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import CommitmentDetailsDialog from "@/components/Inventory/CommitmentDetailsDialog.vue";
import { getInventorySignalR } from "@/services/InventorySignalRService";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "AvailableInventory",
	components: {
		SystemCategoryIcon,
		CommitmentDetailsDialog,
	},
	props: {
		defaultTab: {
			type: Number,
			default: 1,
		},
	},
	data() {
		return {
			loading: true,
			activeTab: this.defaultTab,
			searchForm: {
				searchTerm: "",
				filterInactive: false,
				belowRestockOnly: false,
			},
			inventoryItems: [],
			totalCount: 0,
			expandedItems: [],
			searchTimeout: null,
			// Cache for size inventory data loaded on-demand when rows expand
			sizeDataCache: {},
			sizeDataLoading: {},
			signalRUnsubscribe: null,
			signalRRefreshDebounceTimer: null,
			changedCells: {},
			changeHighlightTimer: null,
			preRefreshSnapshot: null,
			commitmentDialogOpen: false,
			commitmentDialogItemId: null,
			commitmentDialogSizeId: null,
			commitmentDialogSizeName: "",
			commitmentDialogStyleCode: "",
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["styleCode"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Style Code",
					value: "styleCode",
					sortable: true,
					width: "12%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "18%",
				},
				{
					text: "Available",
					value: "available",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "8%",
				},
				{
					text: "On Hand",
					value: "onHand",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "8%",
				},
				{
					text: "Committed",
					value: "committed",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "8%",
				},
				{
					text: "Need",
					value: "need",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "8%",
				},
				{
					text: "On Order",
					value: "onOrder",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "8%",
				},
				{
					text: "Backorder",
					value: "backorder",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "8%",
				},
				{
					text: "Bin #",
					value: "binNumber",
					sortable: true,
					width: "8%",
				},
				{
					text: "Restock",
					value: "restockStatus",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "",
					value: "data-table-expand",
					sortable: false,
					width: "4%",
				},
			],
		};
	},
	apollo: {
		inventoryQuery: {
			query: () => GET_INVENTORY_LISTINGS,
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.response?.totalCount || 0;
				const newItems = data.response?.items || [];

				// Diff against snapshot if this is a SignalR-triggered refetch
				if (this.preRefreshSnapshot) {
					const changes = this.diffInventoryChanges(newItems);
					this.preRefreshSnapshot = null;

					if (Object.keys(changes).length > 0) {
						this.changedCells = changes;
						if (this.changeHighlightTimer) {
							clearTimeout(this.changeHighlightTimer);
						}
						this.changeHighlightTimer = setTimeout(() => {
							this.changedCells = {};
						}, 4000);
					}
				} else {
					// Manual refetch — clear any existing highlights
					this.changedCells = {};
					if (this.changeHighlightTimer) {
						clearTimeout(this.changeHighlightTimer);
						this.changeHighlightTimer = null;
					}
				}

				this.inventoryItems = newItems;
				// Preserve expanded rows across refreshes (including background
				// SignalR-triggered ones) and refresh their size data in place.
				this.syncExpandedAfterRefresh(newItems);
				return data.response;
			},
			error(error) {
				console.error("Error fetching inventory items:", error);
				this.loading = false;
			},
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		$route: {
			handler: "handleRouteChange",
			immediate: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/availableInventoryState"];
					if (savedState) {
						// Restore page from offset
						const page = Math.floor(savedState.offset / this.tableOptions.itemsPerPage) + 1;
						this.tableOptions.page = page;
						this.searchForm.searchTerm = savedState.searchTerm;
						this.searchForm.filterInactive = savedState.filterInactive;
						this.activeTab = savedState.activeTab;
					}
				}
			},
			immediate: true,
		},
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
		canEditCommitments() {
			const user = this.$store.getters.userInfo;
			return hasPermission(user, SystemPermissions.Inventory_BinUtility_Update) || user.isAdmin;
		},
	},
	mounted() {
		const signalR = getInventorySignalR();
		this.signalRUnsubscribe = signalR.subscribe(() => {
			this.onInventoryDataUpdated();
		});
	},
	methods: {
		onInventoryDataUpdated() {
			if (this.signalRRefreshDebounceTimer) {
				clearTimeout(this.signalRRefreshDebounceTimer);
			}
			this.signalRRefreshDebounceTimer = setTimeout(() => {
				console.log("[AvailableInventory] Refreshing data after inventory update");
				this.preRefreshSnapshot = this.buildInventorySnapshot();
				// Note: expanded rows are intentionally NOT collapsed here — the
				// expansion (and its size data) is preserved/refreshed in
				// syncExpandedAfterRefresh so the view holds while the user works.
				this.fetchData();
			}, 2000);
		},

		switchTab(activeIndex) {
			if (this.activeTab !== activeIndex) {
				this.activeTab = activeIndex;
				this.tableOptions.page = 1;
				this.expandedItems = [];

				const path = activeIndex === 1 ? "stocked" : "all";
				this.$router.push(`/inventory/available-inventory/${path}`);
				this.fetchData();
			}
		},

		handleRouteChange() {
			const path = this.$route.path;
			if (path.endsWith("/all")) {
				this.activeTab = 2;
			} else if (path.endsWith("/stocked")) {
				this.activeTab = 1;
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.expandedItems = [];
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.expandedItems = [];
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.expandedItems = [];
			this.fetchData();
		},

		// Map table column values to GraphQL sort field names
		// All quantity columns are now flat columns in InventoryListing view - directly sortable
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				styleCode: "styleCode",
				description: "description",
				available: "available",
				onHand: "onHand",
				committed: "committed",
				need: "need",
				onOrder: "onOrder",
				backorder: "backorder",
				binNumber: "binNumber",
				restockStatus: "restockStatus",
			};
			return sortFieldMap[columnValue] || "styleCode";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.searchTerm || "").trim();

			// Build where clause
			const where = {};

			// Add tab filter
			if (this.activeTab === 1) {
				where.stockItems = { eq: true };
			}

			// Add inactive filter
			if (this.searchForm.filterInactive) {
				where.inactiveDate = { neq: null };
			} else {
				where.inactiveDate = { eq: null };
			}

				// Below/near restock filter
				if (this.searchForm.belowRestockOnly) {
					where.restockStatus = { in: ["Below", "Near"] };
				}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "styleCode";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				where,
				// Use searchTerm parameter for full-text search (InventoryListings uses CONTAINSTABLE)
				// Only pass when not empty to avoid unnecessary search overhead
				searchTerm: keyword || null,
			};
		},

		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.inventoryQuery) {
				await this.$apollo.queries.inventoryQuery.refetch();
			}
		},

		// Handle expand button click - load size data on-demand then expand
		async onExpandClick(item, expand, isExpanded) {
			if (!isExpanded) {
				// Loading size data before expanding
				await this.loadSizeData(item);
			}
			expand(!isExpanded);
		},

		// Check if item has sizes using flat hasSizes column from InventoryListing view
		hasSizes(item) {
			return item.hasSizes === true;
		},

		// Get visible sizes from cached size data
		// Filters by showSize === true to match original behavior
		getVisibleSizes(item) {
			const sizes = this.sizeDataCache[item.inventoryItemId];
			if (!sizes) return [];
			// Filter to only show sizes where showSize is true (like original sizeChart.sizes filter).
			// Hide sizes retired by a size chart change (#1426) — unless they still hold
			// on-hand or committed stock, so physical inventory never silently disappears.
			return sizes.filter(
				(s) => s.showSize === true && (!s.isRetired || (s.onHand || 0) !== 0 || (s.committed || 0) !== 0)
			);
		},

		// Get all sizes (including hidden) for calculations
		getAllSizes(item) {
			return this.sizeDataCache[item.inventoryItemId] || [];
		},

		// Fetch ALL sizes for an item, sorted by sequence to maintain size
		// chart order (not alphabetical). Filtering is done client-side.
		async fetchSizeData(itemId) {
			const { data } = await this.$apollo.query({
				query: GET_SIZE_INVENTORY_INFO,
				variables: {
					// Fetch all sizes for this item - filtering done client-side
					where: { inventoryItemId: { eq: itemId } },
					// Use default order from query (sequence: ASC) to maintain size chart order
				},
				fetchPolicy: "network-only",
			});

			// Client-side sort by sequence as fallback (in case SQL view not updated yet)
			// This ensures sizes are displayed in size chart order, not alphabetically
			const sizes = data.response?.items || [];
			return sizes.slice().sort((a, b) => {
				const seqA = a.sequence ?? 1000;
				const seqB = b.sequence ?? 1000;
				return seqA - seqB;
			});
		},

		// Load size inventory data on-demand when row expands
		async loadSizeData(item) {
			const itemId = item.inventoryItemId;

			// Return if already cached or currently loading
			if (this.sizeDataCache[itemId] || this.sizeDataLoading[itemId]) {
				return;
			}

			// Don't load if item doesn't have sizes
			if (!item.hasSizes) {
				return;
			}

			this.$set(this.sizeDataLoading, itemId, true);

			try {
				const sizes = await this.fetchSizeData(itemId);
				this.$set(this.sizeDataCache, itemId, sizes);
			} catch (error) {
				console.error("Error loading size inventory data:", error);
				this.$set(this.sizeDataCache, itemId, []);
			} finally {
				this.$set(this.sizeDataLoading, itemId, false);
			}
		},

		// Refresh an expanded row's size data after a background refresh.
		// Replaces the cached sizes in place (without toggling the loading
		// spinner) so the expanded chart updates without flickering.
		async refreshSizeDataInPlace(item) {
			if (!item.hasSizes) {
				return;
			}
			const itemId = item.inventoryItemId;
			try {
				const sizes = await this.fetchSizeData(itemId);
				this.$set(this.sizeDataCache, itemId, sizes);
			} catch (error) {
				console.error("Error refreshing size inventory data:", error);
			}
		},

		// After data is refetched, remap the expanded rows onto the new item
		// object references so the expansion survives background (SignalR)
		// refreshes, and refresh their size data in place. Rows that are no
		// longer expanded (or no longer present) have their cached size data
		// dropped.
		syncExpandedAfterRefresh(newItems) {
			const expandedIds = new Set(this.expandedItems.map((i) => i.inventoryItemId));
			const stillExpanded = newItems.filter((i) => expandedIds.has(i.inventoryItemId));

			this.expandedItems = stillExpanded;
			this.sizeDataLoading = {};

			// Keep the previously loaded sizes visible until fresh data replaces
			// them (avoids an empty-table flicker), then refresh in place.
			const preservedCache = {};
			for (const item of stillExpanded) {
				const itemId = item.inventoryItemId;
				if (this.sizeDataCache[itemId]) {
					preservedCache[itemId] = this.sizeDataCache[itemId];
				}
				this.refreshSizeDataInPlace(item);
			}
			this.sizeDataCache = preservedCache;
		},

		// Calculate available as onHand - committed
		// Uses flat columns from InventoryListing view
		calculateTotalAvailable(item) {
			if (item.infiniteInventory) {
				return null; // Will show infinity icon
			}
			const onHand = item.onHand || 0;
			const committed = item.committed || 0;
			const available = onHand - committed;
			return available < 0 ? 0 : available;
		},

		// Calculate available for a size row
		calculateSizeAvailable(size, item) {
			if (item.infiniteInventory) {
				return null; // Will show infinity icon
			}
			const onHand = size.onHand || 0;
			const committed = size.committed || 0;
			const available = onHand - committed;
			return available < 0 ? 0 : available;
		},

		// Format available value for display (handles null and negative values)
		getDisplayAvailable(available) {
			if (available == null) return 0;
			return available < 0 ? 0 : available;
		},

		// Build a snapshot of current displayed values for change detection
		buildInventorySnapshot() {
			const snapshot = {};
			const fields = ["available", "onHand", "committed", "need", "onOrder", "backorder"];
			for (const item of this.inventoryItems) {
				if (item.infiniteInventory) continue;
				const values = {};
				for (const f of fields) {
					values[f] = f === "available" ? this.getDisplayAvailable(item.available) : (item[f] || 0);
				}
				snapshot[item.inventoryItemId] = values;
			}
			return snapshot;
		},

		// Compare new items against snapshot and return map of changed cells
		diffInventoryChanges(newItems) {
			const snapshot = this.preRefreshSnapshot;
			if (!snapshot) return {};

			const changes = {};
			const fields = ["available", "onHand", "committed", "need", "onOrder", "backorder"];

			for (const item of newItems) {
				if (item.infiniteInventory) continue;
				const old = snapshot[item.inventoryItemId];
				if (!old) continue;

				const itemChanges = {};
				let hasChanges = false;

				for (const f of fields) {
					const newVal = f === "available" ? this.getDisplayAvailable(item.available) : (item[f] || 0);
					if (newVal > old[f]) {
						itemChanges[f] = "increase";
						hasChanges = true;
					} else if (newVal < old[f]) {
						itemChanges[f] = "decrease";
						hasChanges = true;
					}
				}

				if (hasChanges) {
					changes[item.inventoryItemId] = itemChanges;
				}
			}
			return changes;
		},

		openCommitmentDialog(item, sizeId, sizeName) {
			this.commitmentDialogItemId = item.inventoryItemId;
			this.commitmentDialogSizeId = sizeId;
			this.commitmentDialogSizeName = sizeName || "";
			this.commitmentDialogStyleCode = item.styleCode || "";
			this.commitmentDialogOpen = true;
		},
		onCommitmentsRemoved() {
			this.sizeDataCache = {};
			this.sizeDataLoading = {};
			this.expandedItems = [];
			this.fetchData();
		},

		// Return CSS class for a cell based on change direction
		getCellChangeClass(item, field) {
			const change = this.changedCells[item.inventoryItemId]?.[field];
			if (!change) return "";
			return change === "increase" ? "cell-increase" : "cell-decrease";
		},
	},
	beforeRouteLeave(to, from, next) {
		const offset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
		this.$store.dispatch("globalState/saveAvailableInventoryState", {
			activeTab: this.activeTab,
			offset,
			searchTerm: this.searchForm.searchTerm,
			filterInactive: this.searchForm.filterInactive,
			scrollPosition: window.scrollY,
		});
		next();
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		if (this.signalRRefreshDebounceTimer) {
			clearTimeout(this.signalRRefreshDebounceTimer);
		}
		if (this.changeHighlightTimer) {
			clearTimeout(this.changeHighlightTimer);
		}
		if (this.signalRUnsubscribe) {
			this.signalRUnsubscribe();
			this.signalRUnsubscribe = null;
		}
	},
};
</script>

<style lang="scss" scoped>
// Page layout
.available-inventory-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.available-inventory-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.available-inventory-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.available-inventory-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

// Tab Navigation
.inventory-tabs-row {
	border-bottom: 1px solid $color-gray;
}

.inventory-tabs {
	list-style: none;
	display: inline-flex;
	flex-flow: row nowrap;
	padding: 0;
	margin: 0;
	background-color: $color-white;

	&-tab {
		padding: 1rem 1.5rem;
		font-size: 1.5rem;
		font-weight: 500;
		color: $color-gray;
		border-bottom: 3px solid transparent;
		margin-bottom: -1px;
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			color: $color-gray-dark;
			background-color: $color-gray-light-2;
		}

		&.active {
			color: $color-tertiary;
			border-bottom: 3px solid $color-tertiary;
			background-color: transparent;
		}
	}
}

// Data Table Styling
.available-inventory-data-table {
	border-radius: 0 0 4px 4px !important;
	border: 1px solid $color-gray; // delimits the grid now that expanded size panels are light (#411)
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 1100px;
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Row styling
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}

	// Expanded row styling
	::v-deep > .v-data-table__wrapper > table > tbody > tr.v-data-table__expanded__content {
		box-shadow: none;

		> td {
			padding: 0 !important;
		}
	}
}

// Change highlight for real-time inventory updates
.inv-cell {
	transition: color 0.8s ease;
}

.cell-increase {
	color: #00c853 !important;
	font-weight: 700;
}

.cell-decrease {
	color: #ff1744 !important;
	font-weight: 700;
}

.committed-link {
	color: #1565c0;
	cursor: pointer;
	border-bottom: 1px solid transparent;
	transition: border-color 0.15s ease;

	&:hover {
		border-bottom-color: #1565c0;
	}
}

// Size Chart Styling
.inventory-size-chart {
	background-color: $color-gray-light; // light panel so the gray size column stands out, as in the sizing dialog (#411)
	padding: 1.5rem 4%;

	.size-chart-table {
		border-radius: 5px;
		overflow: hidden;
		border: 1px solid $color-gray;

		::v-deep table {
			border-collapse: collapse;
			width: 100%;
		}

		::v-deep th,
		::v-deep td {
			width: calc(100% / 8); // Distribute 8 columns evenly
		}

		::v-deep thead tr {
			background-color: $color-gray-dark !important;

			th {
				color: $color-white !important;
				font-size: 1.3rem !important;
				font-weight: 500 !important;
				text-align: center !important;
				padding: 12px 16px !important;
				border-bottom: none !important;
				background-color: $color-gray-dark !important;
			}
		}

		::v-deep tbody {
			background-color: $color-white;

			tr.size-chart-row {
				td {
					text-align: center !important;
					font-size: 1.4rem !important;
					padding: 10px 16px !important;
					border-bottom: 1px solid $color-gray !important;
				}

				// Matches the sizing dialog's gray size cells instead of the old orange, which
				// clashed with the rest of the page (#411)
				td.size-name-cell {
					background-color: $color-gray;
					font-weight: 500;
					border-bottom-color: darken($color-gray, 15%) !important; // keeps the row divider visible on the gray column
				}
			}

			tr.size-chart-total-row {
				background-color: $color-gray-2; // same total-row tone as the sizing dialog

				td {
					text-align: center !important;
					font-size: 1.4rem !important;
					font-weight: 600 !important;
					padding: 10px 16px !important;
					border-bottom: none !important;
				}

				td.size-name-cell {
					background-color: $color-gray;
				}
			}
		}
	}
}

.restock-cell {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.6rem;
}
.restock-qty-inline {
	font-size: 1.3rem;
	font-weight: 600;
	opacity: 0.8;
	white-space: nowrap;
}
.restock-chip {
	font-size: 1.2rem;
	font-weight: 600;
	white-space: nowrap;
}
.restock-below {
	color: #c62828;
}
.restock-near {
	color: #ef6c00;
}
::v-deep .size-chart-row.restock-below-row {
	background-color: rgba(198, 40, 40, 0.08);
}
::v-deep .size-chart-row.restock-near-row {
	background-color: rgba(239, 108, 0, 0.08);
}
</style>
